import React, { useEffect, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { Link, useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import {
  GET_INVENTORY_INGREDIENT,
  CREATE_INVENTORY_INGREDIENT,
  UPDATE_INVENTORY_INGREDIENT,
  DELETE_INVENTORY_INGREDIENT,
  ADJUST_INVENTORY_STOCK,
  CREATE_UNIT_CONVERSION,
  DELETE_UNIT_CONVERSION,
} from "./queries";
import { GET_BRANCHES } from "../account/queries";
import {
  UNIT_OPTIONS,
  LOCATION_OPTIONS,
  CATEGORY_OPTIONS,
  InventoryIngredientLocation,
  InventoryMovement,
  InventoryUnitConversion,
  MOVEMENT_TYPE_LABELS,
} from "./models";
import { DeleteModal } from "../../modals/deletes";

type Params = {
  id?: string;
};

const IngredientForm: React.FC = () => {
  const { id } = useParams<Params>();
  const history = useHistory();
  const isEdit = !!id;

  // Form state
  const [name, setName] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [sku, setSku] = useState("");
  const [unit, setUnit] = useState("pz");
  const [cost, setCost] = useState("");
  const [category, setCategory] = useState("");
  const [lowStockThreshold, setLowStockThreshold] = useState("");
  const [reorderPoint, setReorderPoint] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  // Adjust stock state
  const [adjustBranch, setAdjustBranch] = useState("");
  const [adjustLocation, setAdjustLocation] = useState("kitchen");
  const [adjustQty, setAdjustQty] = useState("");
  const [adjustNotes, setAdjustNotes] = useState("");

  // Conversion state
  const [convFromUnit, setConvFromUnit] = useState("box");
  const [convToUnit, setConvToUnit] = useState("pz");
  const [convFactor, setConvFactor] = useState("");

  // Queries & mutations
  const { data: branchesData } = useQuery(GET_BRANCHES);
  const [fetchIngredient, { data, loading }] = useLazyQuery(
    GET_INVENTORY_INGREDIENT,
    { fetchPolicy: "no-cache" }
  );
  const [createIngredient] = useMutation(CREATE_INVENTORY_INGREDIENT);
  const [updateIngredient] = useMutation(UPDATE_INVENTORY_INGREDIENT);
  const [deleteIngredient] = useMutation(DELETE_INVENTORY_INGREDIENT);
  const [adjustStock] = useMutation(ADJUST_INVENTORY_STOCK);
  const [createConversion] = useMutation(CREATE_UNIT_CONVERSION);
  const [deleteConversion] = useMutation(DELETE_UNIT_CONVERSION);

  useEffect(() => {
    if (isEdit) {
      fetchIngredient({ variables: { id } });
    }
    //eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (data?.getInventoryIngredient) {
      const ing = data.getInventoryIngredient;
      setName(ing.name);
      setNameEn(ing.name_en || "");
      setSku(ing.sku || "");
      setUnit(ing.unit);
      setCost(ing.cost?.toString() || "");
      setCategory(ing.category || "");
      setLowStockThreshold(ing.low_stock_threshold?.toString() || "");
      setReorderPoint(ing.reorder_point?.toString() || "");
    }
  }, [data]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(lang("El nombre es obligatorio", "Name is required"));
      return;
    }
    try {
      const variables: any = {
        name,
        name_en: nameEn || null,
        sku: sku || null,
        unit,
        cost: cost ? parseFloat(cost) : null,
        category: category || null,
        low_stock_threshold: lowStockThreshold
          ? parseFloat(lowStockThreshold)
          : null,
        reorder_point: reorderPoint ? parseFloat(reorderPoint) : null,
      };
      if (isEdit) {
        variables.id = id;
        await updateIngredient({ variables });
        toast.success(lang("Ingrediente actualizado", "Ingredient updated"));
        fetchIngredient({ variables: { id } });
      } else {
        await createIngredient({ variables });
        toast.success(lang("Ingrediente creado", "Ingredient created"));
        history.push("/inventory");
      }
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteIngredient({ variables: { id } });
      toast.success(lang("Ingrediente eliminado", "Ingredient deleted"));
      history.push("/inventory");
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleAdjust = async () => {
    if (!adjustBranch || !adjustQty) {
      toast.error(
        lang(
          "Selecciona sucursal y cantidad",
          "Select branch and quantity"
        )
      );
      return;
    }
    try {
      await adjustStock({
        variables: {
          ingredient_id: id,
          branch_id: parseInt(adjustBranch),
          location: adjustLocation,
          new_quantity: parseFloat(adjustQty),
          notes: adjustNotes || null,
        },
      });
      toast.success(lang("Stock ajustado", "Stock adjusted"));
      setAdjustQty("");
      setAdjustNotes("");
      fetchIngredient({ variables: { id } });
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleAddConversion = async () => {
    if (!convFactor || parseFloat(convFactor) <= 0) {
      toast.error(lang("Factor inválido", "Invalid factor"));
      return;
    }
    try {
      await createConversion({
        variables: {
          ingredient_id: id,
          from_unit: convFromUnit,
          to_unit: convToUnit,
          factor: parseFloat(convFactor),
        },
      });
      toast.success(lang("Conversión agregada", "Conversion added"));
      setConvFactor("");
      fetchIngredient({ variables: { id } });
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleDeleteConversion = async (convId: string) => {
    try {
      await deleteConversion({ variables: { id: convId } });
      fetchIngredient({ variables: { id } });
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  if (loading) {
    return <LoaderDots fullSize />;
  }

  const ingredient = data?.getInventoryIngredient;
  const branches: any[] = branchesData?.getBranches || [];

  return (
    <>
      <p>
        <Link to="/inventory">
          <i className="fa fa-chevron-left" aria-hidden="true"></i>{" "}
          {lang("Inventario", "Inventory")}
        </Link>
      </p>
      <h1>
        {isEdit
          ? lang("Editar Ingrediente", "Edit Ingredient")
          : lang("Nuevo Ingrediente", "New Ingredient")}
      </h1>

      <div className="row">
        {/* Main form */}
        <div className="col-lg-6">
          <div className="material_card p-3 mb-3">
            <h4>{lang("Datos del ingrediente", "Ingredient details")}</h4>
            <div className="form-group" data-tour="ingredient-name">
              <label>{lang("Nombre", "Name")} *</label>
              <input
                type="text"
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={lang("Ej: Carne molida", "E.g. Ground beef")}
              />
            </div>
            <div className="form-group">
              <label>{lang("Nombre en inglés", "English name")}</label>
              <input
                type="text"
                className="form-control"
                value={nameEn}
                onChange={(e) => setNameEn(e.target.value)}
              />
            </div>
            <div className="row">
              <div className="col-6">
                <div className="form-group">
                  <label>SKU</label>
                  <input
                    type="text"
                    className="form-control"
                    value={sku}
                    onChange={(e) => setSku(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-6">
                <div className="form-group" data-tour="ingredient-unit">
                  <label>{lang("Unidad", "Unit")} *</label>
                  <select
                    className="custom-select"
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                  >
                    {UNIT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-6">
                <div className="form-group" data-tour="ingredient-cost">
                  <label>{lang("Costo por unidad", "Cost per unit")}</label>
                  <input
                    type="number"
                    className="form-control"
                    value={cost}
                    onChange={(e) => setCost(e.target.value)}
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>
              <div className="col-6">
                <div className="form-group" data-tour="ingredient-category">
                  <label>{lang("Categoría", "Category")}</label>
                  <select
                    className="custom-select"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    <option value="">—</option>
                    {CATEGORY_OPTIONS.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {lang(cat.label, cat.labelEn)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-6">
                <div className="form-group" data-tour="ingredient-threshold">
                  <label>{lang("Alerta stock bajo", "Low stock alert")}</label>
                  <input
                    type="number"
                    className="form-control"
                    value={lowStockThreshold}
                    onChange={(e) => setLowStockThreshold(e.target.value)}
                    step="0.01"
                    min="0"
                  />
                  <small className="form-text text-muted">
                    {lang(
                      "Se marca en rojo cuando el stock baje de este valor",
                      "Highlighted red when stock falls below this"
                    )}
                  </small>
                </div>
              </div>
              <div className="col-6">
                <div className="form-group">
                  <label>{lang("Punto de reorden", "Reorder point")}</label>
                  <input
                    type="number"
                    className="form-control"
                    value={reorderPoint}
                    onChange={(e) => setReorderPoint(e.target.value)}
                    step="0.01"
                    min="0"
                  />
                  <small className="form-text text-muted">
                    {lang(
                      "Sugiere compra cuando el stock baje de aquí",
                      "Suggests purchase when stock falls below"
                    )}
                  </small>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-between mt-3">
              {isEdit && (
                <button
                  className="btn btn-outline-danger"
                  onClick={() => setShowDelete(true)}
                >
                  <i className="fa fa-trash"></i>{" "}
                  {lang("Eliminar", "Delete")}
                </button>
              )}
              <button className="btn btn-primary" data-tour="ingredient-save" onClick={handleSave}>
                <i className="far fa-save"></i>{" "}
                {lang("Guardar", "Save")}
              </button>
            </div>
          </div>
        </div>

        {/* Stock levels + Adjustments (edit only) */}
        {isEdit && ingredient && (
          <div className="col-lg-6">
            {/* Stock levels */}
            <div className="material_card p-3 mb-3">
              <h4>{lang("Niveles de Stock", "Stock Levels")}</h4>
              {ingredient.locations && ingredient.locations.length > 0 ? (
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>{lang("Sucursal", "Branch")}</th>
                      <th>{lang("Ubicación", "Location")}</th>
                      <th>{lang("Cantidad", "Quantity")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ingredient.locations.map(
                      (loc: InventoryIngredientLocation) => (
                        <tr key={loc.id}>
                          <td>{loc.branch?.name || `${lang("Sucursal", "Branch")} ${loc.branch_id}`}</td>
                          <td>
                            <span className="badge badge-light">
                              {loc.location}
                            </span>
                          </td>
                          <td>
                            {Number(loc.quantity).toFixed(2)} {ingredient.unit}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="font-weight-bold">
                      <td colSpan={2}>{lang("Total", "Total")}</td>
                      <td>
                        {Number(ingredient.total_stock || 0).toFixed(2)}{" "}
                        {ingredient.unit}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <p className="text-muted">
                  {lang("Sin stock registrado", "No stock registered")}
                </p>
              )}
            </div>

            {/* Quick stock adjustment */}
            <div className="material_card p-3 mb-3">
              <h4>{lang("Ajuste rápido de stock", "Quick stock adjustment")}</h4>
              <div className="row">
                <div className="col-6">
                  <div className="form-group">
                    <label>{lang("Sucursal", "Branch")}</label>
                    <select
                      className="custom-select"
                      value={adjustBranch}
                      onChange={(e) => setAdjustBranch(e.target.value)}
                    >
                      <option value="">{lang("Seleccionar", "Select")}</option>
                      {(branches as any[]).map((b: any) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group">
                    <label>{lang("Ubicación", "Location")}</label>
                    <select
                      className="custom-select"
                      value={adjustLocation}
                      onChange={(e) => setAdjustLocation(e.target.value)}
                    >
                      {LOCATION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <div className="form-group">
                    <label>
                      {lang("Nueva cantidad", "New quantity")} ({ingredient.unit})
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={adjustQty}
                      onChange={(e) => setAdjustQty(e.target.value)}
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group">
                    <label>{lang("Notas", "Notes")}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={adjustNotes}
                      onChange={(e) => setAdjustNotes(e.target.value)}
                      placeholder={lang("Conteo físico", "Physical count")}
                    />
                  </div>
                </div>
              </div>
              <button className="btn btn-warning btn-sm" onClick={handleAdjust}>
                <i className="fas fa-balance-scale"></i>{" "}
                {lang("Ajustar Stock", "Adjust Stock")}
              </button>
            </div>

            {/* Unit conversions */}
            <div className="material_card p-3 mb-3">
              <h4>
                {lang("Conversiones de unidad", "Unit conversions")}
              </h4>
              {ingredient.conversions &&
                ingredient.conversions.map((conv: InventoryUnitConversion) => (
                  <div
                    key={conv.id}
                    className="d-flex justify-content-between align-items-center mb-2"
                  >
                    <span>
                      1 {conv.from_unit} = {conv.factor} {conv.to_unit}
                    </span>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDeleteConversion(conv.id)}
                    >
                      <i className="fa fa-times"></i>
                    </button>
                  </div>
                ))}
              <hr />
              <div className="row">
                <div className="col-4">
                  <select
                    className="custom-select custom-select-sm"
                    value={convFromUnit}
                    onChange={(e) => setConvFromUnit(e.target.value)}
                  >
                    {UNIT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.value}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-4">
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    placeholder={lang("Factor", "Factor")}
                    value={convFactor}
                    onChange={(e) => setConvFactor(e.target.value)}
                    step="0.01"
                    min="0"
                  />
                </div>
                <div className="col-4">
                  <select
                    className="custom-select custom-select-sm"
                    value={convToUnit}
                    onChange={(e) => setConvToUnit(e.target.value)}
                  >
                    {UNIT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                className="btn btn-sm btn-outline-primary mt-2"
                onClick={handleAddConversion}
              >
                <i className="fa fa-plus"></i>{" "}
                {lang("Agregar", "Add")}
              </button>
            </div>

            {/* Recent movements */}
            {ingredient.movements && ingredient.movements.length > 0 && (
              <div className="material_card p-3 mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h4 className="mb-0">
                    {lang("Movimientos recientes", "Recent movements")}
                  </h4>
                  <Link
                    to={`/inventory/movements/${id}`}
                    className="btn btn-sm btn-outline-primary"
                  >
                    {lang("Ver todos", "View all")}
                  </Link>
                </div>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>{lang("Fecha", "Date")}</th>
                      <th>{lang("Tipo", "Type")}</th>
                      <th>{lang("Cantidad", "Qty")}</th>
                      <th>{lang("Notas", "Notes")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ingredient.movements
                      .slice(0, 10)
                      .map((mov: InventoryMovement) => {
                        const typeInfo =
                          MOVEMENT_TYPE_LABELS[mov.movement_type] || {
                            label: mov.movement_type,
                            color: "secondary",
                          };
                        return (
                          <tr key={mov.id}>
                            <td>
                              <small>
                                {new Date(
                                  parseInt(mov.created_at)
                                ).toLocaleDateString()}
                              </small>
                            </td>
                            <td>
                              <span
                                className={`badge badge-${typeInfo.color}`}
                              >
                                {typeInfo.label}
                              </span>
                            </td>
                            <td
                              className={
                                mov.quantity_change >= 0
                                  ? "text-success"
                                  : "text-danger"
                              }
                            >
                              {mov.quantity_change >= 0 ? "+" : ""}
                              {Number(mov.quantity_change).toFixed(2)}
                            </td>
                            <td>
                              <small className="text-muted">
                                {mov.notes || "—"}
                              </small>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <DeleteModal
        open={showDelete}
        title={lang(
          `¿Eliminar ingrediente "${name}"?`,
          `Delete ingredient "${name}"?`
        )}
        toggle={() => setShowDelete(false)}
        handleConfirm={handleDelete}
        confirm_txt={lang("Eliminar", "Delete")}
      >
        <p>
          {lang(
            "El ingrediente se desactivará y no aparecerá en las listas",
            "The ingredient will be deactivated and won't appear in lists"
          )}
        </p>
      </DeleteModal>
    </>
  );
};

export default IngredientForm;
